import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TrendingUp,
  ChevronLeft,
  Menu,
  X,
  Home,
  Settings,
  ExternalLink,
  Sun,
  Moon,
  LogOut,
  Shield,
  School
} from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { UserInfoSection } from '../layout/UserInfoSection';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';
import { employeeNamesService } from '../../services/employeeNames';
import { FarmingReviewForm } from './FarmingReviewForm';
import '../../styles/farming-results-theme.css';

interface FarmingResultsLayoutProps {
  children: React.ReactNode;
}

const FarmingResultsLayout: React.FC<FarmingResultsLayoutProps> = ({ children }) => {
  const [displayName, setDisplayName] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isAdmin } = useIsAdmin();
  const { 
    companies, 
    selectedCompany, 
    setSelectedCompany,
    products,
    selectedProduct,
    setSelectedProduct,
    loading,
    error 
  } = useFarmingResultsSelection();

  // Load display name
  useEffect(() => {
    const loadDisplayName = async () => {
      if (user?.email) {
        try {
          const name = await employeeNamesService.getNameFromEmail(user.email);
          if (name) {
            setDisplayName(name);
          } else {
            // Fallback to email formatting
            const emailName = user.email.split('@')[0];
            const formattedName = emailName
              .split('.')
              .map(part => part.charAt(0).toUpperCase() + part.slice(1))
              .join(' ');
            setDisplayName(formattedName);
          }
        } catch (error) {
          console.error('Error loading display name:', error);
          setDisplayName(user.email.split('@')[0]);
        }
      }
    };

    loadDisplayName();
  }, [user?.email]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navigateToAppsHub = () => {
    navigate('/dashboard');
    setIsMobileMenuOpen(false);
  };

  const navigateToSettings = () => {
    navigate('/farming-results/settings');
    setIsMobileMenuOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="theme-farming-results h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          )}
        </button>
        <h1 className="ml-4 text-lg font-semibold text-gray-900 dark:text-white">
          Farming Results
        </h1>
      </div>

      {/* Mobile Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
        transform transition-transform duration-300 ease-in-out flex flex-col
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
        ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
      `}>
        {/* Collapse Toggle Button - Desktop Only */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block z-50"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        {/* App Header - Match PMT3 height */}
        <div
          className="border-b dark:border-gray-700 flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => navigate('/farming-results')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <TrendingUp className="h-12 w-12 text-app-primary mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <TrendingUp className="h-20 w-20 text-app-primary mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Farming Results</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Amazon Farming Metrics</p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation - Company/ASIN Selection */}
          <div className={`px-4 py-2 mb-4 space-y-2 ${isCollapsed && !isMobileMenuOpen && window.innerWidth >= 768 ? 'hidden' : 'block'}`}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Select Company</label>
              <Select
                value={selectedCompany}
                onValueChange={setSelectedCompany}
                disabled={loading}
              >
                <SelectTrigger className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => (
                    <SelectItem key={company} value={company}>
                      {company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Select Product (ASIN)</label>
              <Select
                value={selectedProduct?.id || ''}
                onValueChange={(value) => {
                  const product = products.find(p => p.id === value);
                  setSelectedProduct(product || null);
                }}
                disabled={!selectedCompany || products.length === 0}
              >
                <SelectTrigger className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {`${product.id}${product.Title ? ` - ${product.Title.substring(0, 30)}${product.Title.length > 30 ? '...' : ''}` : ''}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* First Divider */}
          <div className="mx-4 mb-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Dashboard */}
          <div
            onClick={() => navigate('/farming-results')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/farming-results') && location.pathname === '/farming-results'
                ? 'bg-app-primary/10 dark:bg-app-primary/20 text-app-primary dark:text-app-primary'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <TrendingUp className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={navigateToSettings}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/farming-results/settings')
                ? 'bg-app-primary/10 dark:bg-app-primary/20 text-app-primary dark:text-app-primary'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => navigate('/admin')}
              className="flex items-center px-4 py-2 text-sm cursor-pointer text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={navigateToAppsHub}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
          {/* Farming Review Form - Only shown when sidebar is expanded */}
          {(!isCollapsed || isMobileMenuOpen) && (
            <>
              <div className="mx-4 mb-4 border-t border-gray-200 dark:border-gray-700" />
              <FarmingReviewForm />
            </>
          )}
        </nav>

        {/* User Info Section */}
        <UserInfoSection displayName={displayName} isCollapsed={isCollapsed && !isMobileMenuOpen} />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className={`${isMobileMenuOpen ? 'md:pt-0 pt-16' : 'md:pt-0 pt-16 md:pt-0'} p-6`}>
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default FarmingResultsLayout;